import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback } from 'react'
import { Offcanvas } from 'react-bootstrap'
import { useForm } from 'react-hook-form';
import { privateApi } from '../../../api/api';
import { toast } from 'sonner';
import clsx from 'clsx';
import { UpdateRoomTypeSchema } from '../schema/UpdateRoomTypeSchema';
import FormSpinner from '../../../shared/components/spinner/FormSpinner';
import { applyServerValidationErrors } from '../../../shared/utils/serverValidation';

interface UpdateRoomTypeDIalogProps {
    show: boolean,
    onClose: () => void,
    roomTypeId: string
}

interface UpdateRoomTypeForm {
    name: string,
    description: string,
    occ_adults: number,
    occ_children: number,
}

const UpdateRoomTypeDIalog: React.FC<UpdateRoomTypeDIalogProps> = ({ show, onClose, roomTypeId }) => {
    const { data: roomtype } = useQuery({
        queryKey: ['rooms', roomTypeId],
        queryFn: () => privateApi.get(`/v4/room-type/${roomTypeId}`).then((res) => res.data),
        enabled: !!roomTypeId,
    });

    if (roomtype?.isError) {
        toast.error(roomtype.error?.response?.data?.message || 'An error occurred', { id: 'update-room-type' });
    }

    const { register, handleSubmit, formState: { errors }, reset, setError } = useForm<UpdateRoomTypeForm>({
        resolver: zodResolver(UpdateRoomTypeSchema),
        disabled: !!roomtype?.isError || !!roomtype?.isLoading,
        values: {
            name: roomtype?.data?.name || '',
            description: roomtype?.data?.description || '',
            occ_adults: roomtype?.data?.occ_adults || 0,
            occ_children: roomtype?.data?.occ_children || 0,
        },
    });

    const handleClose = () => {
        reset();
        onClose();
    }

    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: (data: {
            name: string,
            description: string,
            occ_adults: number,
            occ_children: number,
        }) => privateApi.put(`/v4/room-type/${roomTypeId}`, data),
        onSuccess: async () => {
            handleClose();
            await queryClient.refetchQueries({
                predicate: (query) => query.queryKey.some((key) => ['rooms', 'rates'].includes(key as string))
            });
            toast.success('Room type updated successfully', { id: 'update-room-type' });
        },
        onError: (error: any) => {
            const handled = applyServerValidationErrors(error, setError, {
                toastId: 'update-room-type',
            });

            if (!handled) {
                toast.error(error?.response?.data?.message || 'An error occurred', { id: 'update-room-type' });
            }
        },
    });

    const onSubmit = useCallback((data: {
        name: string,
        description: string,
        occ_adults: number,
        occ_children: number,
    }) => {
        toast.loading('Updating room type...', { id: 'update-room-type' });
        mutate(data);
    }, [mutate]);

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Create Room Type</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                {!roomtype?.success && <FormSpinner />}
                {roomtype?.success && (
                    <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column" style={{ height: '100%' }}>
                        <div className="action-sheet-content flex-grow-1">
                            <div className="row mb-3 align-items-center">
                                <label htmlFor="name" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Name: </label>
                                <div className="col-sm-8">
                                    <input
                                        type="text"
                                        className={clsx("form-control app-form-control rounded-1", errors.name && 'is-invalid')}
                                        id="name"
                                        placeholder="Name"
                                        {...register('name')}
                                        style={{ fontSize: '14px', height: '35px' }}
                                    />
                                    {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                                </div>
                            </div>

                            <div className="row mb-3 align-items-center">
                                <label htmlFor="description" className="col-sm-4 col-form-label d-flex justify-content-md-end">Description: </label>
                                <div className="col-sm-8">
                                    <textarea
                                        className={clsx("form-control app-form-control rounded-1", errors.description && 'is-invalid')}
                                        id="description"
                                        placeholder="Enter rate plan description"
                                        {...register('description')}
                                    />
                                    {errors.description && <div className="invalid-feedback">{errors.description.message}</div>}
                                </div>
                            </div>

                            <h3 className="mt-6 border-bottom fw-normal">Room Settings</h3>

                            {/* Add Adults input */}
                            <div className="row mb-3 align-items-center">
                                <label htmlFor="adults" className="col-sm-4 col-form-label d-flex justify-content-md-end">Occupancy Adults</label>
                                <div className="col-sm-8">
                                    <input
                                        type="number"
                                        className={clsx("form-control app-form-control rounded-1", errors.occ_adults && 'is-invalid')}
                                        id="adults"
                                        placeholder="Enter no. of adults"
                                        {...register('occ_adults')}
                                    />
                                    {errors.occ_adults && <div className="invalid-feedback">{errors.occ_adults.message}</div>}
                                </div>
                            </div>

                            {/* Add children input */}
                            <div className="row mb-3 align-items-center">
                                <label htmlFor="children" className="col-sm-4 col-form-label d-flex justify-content-md-end">Occupancy Children</label>
                                <div className="col-sm-8">
                                    <input
                                        type="number"
                                        className={clsx("form-control app-form-control rounded-1", errors.occ_children && 'is-invalid')}
                                        id="children"
                                        placeholder="Enter no. of children"
                                        {...register('occ_children')}
                                    />
                                    {errors.occ_children && <div className="invalid-feedback">{errors.occ_children.message}</div>}
                                </div>
                            </div>
                        </div>

                        <div className="form-group mt-auto d-grid">
                            <button type="submit" className="btn btn-primary btn-block" disabled={isPending}>
                                {isPending ? 'Updating room type...' : 'Update Room Type'}
                            </button>
                        </div>
                    </form>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    )
}

export default UpdateRoomTypeDIalog